import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileSignature, Plus, X, Loader, Save, Paperclip, Download, ExternalLink, Trash2, Undo2,
  Pencil, CalendarClock, RefreshCw, ChevronDown, ChevronRight,
} from 'lucide-react';
import { useApp } from '../context/AppContext';

// ── Contract Review Requests ────────────────────────────────────────────────────
// The register of formal requests we make to a customer to review a contract — volume,
// rates, terms. Each request keeps the letter exactly as it was sent, the workbooks that
// prove its figures, the reply, and a dated trail of what happened. Records are never
// thrown away: removing a request or a file hides it (Restore brings it back) and the
// file itself stays in storage.

const API_URL = '/.netlify/functions/contract-reviews-sb';
const DOC_URL = '/.netlify/functions/contract-reviews-doc-sb';
const MAX_MB = 4; // Netlify's 6 MB body less base64's ~33%

const api = (action, payload = {}) =>
  fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const fmtDate = (iso) => (iso ? new Date(iso.slice(0, 10) + 'T12:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });

const STATUS_COLOUR = {
  Draft: 'var(--hp-dim)',
  Sent: 'var(--hp-gold)',
  Acknowledged: 'var(--hp-gold)',
  'In discussion': 'var(--hp-amber, #f59e0b)',
  Agreed: 'var(--hp-green, #22c55e)',
  'Partly agreed': 'var(--hp-green, #22c55e)',
  Declined: 'var(--hp-red, #ef4444)',
  Withdrawn: 'var(--hp-dim)',
};
const Pill = ({ s }) => {
  const c = STATUS_COLOUR[s] || 'var(--hp-dim)';
  return <span style={{ fontSize: '.7rem', fontWeight: 700, color: c, border: `1px solid ${c}`, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>{s}</span>;
};

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };
const sectionTitle = { fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', margin: '16px 0 8px' };

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

const BLANK = {
  title: '', counterparty: 'Australia Post', contract: 'N22000', recipient: '',
  sentDate: '', responseDue: '', status: 'Draft', asked: '', summary: '', outcome: '',
};

// ── Add / edit drawer ───────────────────────────────────────────────────────────
function RequestForm({ request, statuses, onClose, onSaved, notify }) {
  const [form, setForm] = useState(() => ({ ...BLANK, ...(request || {}) }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) { notify('Give the request a title', 'error'); return; }
    setBusy(true);
    try {
      const r = await api('saveRequest', { request: form });
      if (!r.success) throw new Error(r.error);
      notify(request ? 'Request updated' : 'Request added — now attach the letter', 'success');
      onSaved(r.request);
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div style={{ flex: props.flex || '1 1 220px' }}>
      <label style={labelStyle}>{label}</label>
      <input className="input" style={inputStyle} value={form[k] || ''} onChange={set(k)} {...props.input} />
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--hp-gold)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong>{request ? 'Edit request' : 'New contract review request'}</strong>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {field('title', 'Title', { flex: '1 1 100%', input: { placeholder: 'e.g. Volume decline since 11 Sep 2026 — rate review' } })}
        {field('counterparty', 'Customer')}
        {field('contract', 'Contract')}
        {field('recipient', 'Sent to', { input: { placeholder: 'Name, role' } })}
        {field('sentDate', 'Date sent', { flex: '1 1 150px', input: { type: 'date' } })}
        {field('responseDue', 'Response due', { flex: '1 1 150px', input: { type: 'date' } })}
        <div style={{ flex: '1 1 150px' }}>
          <label style={labelStyle}>Status</label>
          <select className="input" style={inputStyle} value={form.status} onChange={set('status')}>
            {statuses.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        {field('asked', 'What we asked for', { flex: '1 1 100%', input: { placeholder: 'e.g. Parcel rate $3.1094 → $3.35 (+7.7%), volume clause, streaming data, meeting' } })}
        <div style={{ flex: '1 1 100%' }}>
          <label style={labelStyle}>Summary</label>
          <textarea className="input" style={{ ...inputStyle, minHeight: 80 }} value={form.summary} onChange={set('summary')} placeholder="The case in a few lines: what changed, what it costs, what we want." />
        </div>
        <div style={{ flex: '1 1 100%' }}>
          <label style={labelStyle}>Outcome</label>
          <textarea className="input" style={{ ...inputStyle, minHeight: 60 }} value={form.outcome} onChange={set('outcome')} placeholder="What the customer agreed or declined, and from when." />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
          {busy ? <Loader size={14} className="spin" /> : <Save size={14} />} Save
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ── One request ─────────────────────────────────────────────────────────────────
function RequestCard({ req, fileKinds, open, onToggle, onEdit, onChanged, notify }) {
  const fileRef = useRef(null);
  const [kind, setKind] = useState('Letter sent');
  const [uploading, setUploading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [ev, setEv] = useState({ date: todayIso(), note: '' });

  const files = (req.files || []).filter((f) => showHidden || !f.deletedAt);
  const events = (req.events || []).filter((e) => showHidden || !e.deletedAt).slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const hiddenCount = (req.files || []).filter((f) => f.deletedAt).length + (req.events || []).filter((e) => e.deletedAt).length;

  const call = async (action, payload, okMsg) => {
    const r = await api(action, { requestId: req.id, ...payload });
    if (!r.success) { notify(r.error || 'Failed', 'error'); return false; }
    onChanged(r.request); if (okMsg) notify(okMsg, 'success');
    return true;
  };

  const upload = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const f of picked) {
        if (f.size > MAX_MB * 1024 * 1024) { notify(`${f.name} is over ${MAX_MB} MB — not uploaded`, 'error'); continue; }
        const base64 = arrayBufferToBase64(await f.arrayBuffer());
        await call('addFile', { base64, contentType: f.type || 'application/octet-stream', fileName: f.name, kind }, `${f.name} saved`);
      }
    } catch (e) { notify(`Upload failed: ${e.message}`, 'error'); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addEvent = async () => {
    if (!ev.note.trim()) { notify('Say what happened', 'error'); return; }
    if (await call('addEvent', ev, 'Added to the timeline')) setEv({ date: todayIso(), note: '' });
  };

  const letter = (req.files || []).find((f) => !f.deletedAt && f.kind === 'Letter sent');

  return (
    <div className="card" style={{ marginBottom: 12, opacity: req.deletedAt ? 0.55 : 1 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }} onClick={onToggle}>
        {open ? <ChevronDown size={18} style={{ marginTop: 2, flexShrink: 0 }} /> : <ChevronRight size={18} style={{ marginTop: 2, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '1rem' }}>{req.title}</strong>
            <Pill s={req.status} />
            {req.deletedAt && <span style={{ fontSize: '.72rem', color: 'var(--hp-red, #ef4444)' }}>Removed</span>}
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginTop: 4 }}>
            {[req.counterparty, req.contract && `Contract ${req.contract}`, req.sentDate ? `Sent ${fmtDate(req.sentDate)}` : 'Not sent yet',
              req.recipient && `to ${req.recipient}`, req.responseDue && `Reply due ${fmtDate(req.responseDue)}`].filter(Boolean).join(' · ')}
          </div>
          {req.asked && <div style={{ fontSize: '.84rem', marginTop: 6 }}><span style={{ color: 'var(--hp-dim)' }}>Asked: </span>{req.asked}</div>}
        </div>
        {letter && (
          <a className="btn btn-secondary btn-sm" href={`${DOC_URL}?id=${req.id}&file=${letter.id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            <ExternalLink size={13} /> Letter
          </a>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 10, paddingLeft: 28 }}>
          {req.summary && <p style={{ fontSize: '.88rem', whiteSpace: 'pre-wrap', margin: '6px 0' }}>{req.summary}</p>}
          {req.outcome && <p style={{ fontSize: '.88rem', whiteSpace: 'pre-wrap', margin: '6px 0' }}><strong>Outcome: </strong>{req.outcome}</p>}

          <div style={sectionTitle}>Documents</div>
          {files.length === 0 && <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)' }}>Nothing attached yet. Attach the letter as it was sent and the workbooks behind it.</div>}
          {files.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--hp-border)', opacity: f.deletedAt ? 0.5 : 1, flexWrap: 'wrap' }}>
              <Paperclip size={14} style={{ color: 'var(--hp-dim)' }} />
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontSize: '.86rem', overflowWrap: 'anywhere' }}>{f.name}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>{f.kind} · {fmtSize(f.size || 0)} · added {fmtDate(f.addedAt)}{f.addedBy ? ` by ${f.addedBy}` : ''}{f.deletedAt ? ' · removed' : ''}</div>
              </div>
              {!f.deletedAt && <>
                <a className="btn btn-ghost btn-sm" href={`${DOC_URL}?id=${req.id}&file=${f.id}`} target="_blank" rel="noreferrer" title="Open"><ExternalLink size={13} /></a>
                <a className="btn btn-ghost btn-sm" href={`${DOC_URL}?id=${req.id}&file=${f.id}&dl=1`} title="Download"><Download size={13} /></a>
              </>}
              {f.deletedAt
                ? <button className="btn btn-ghost btn-sm" onClick={() => call('setFileDeleted', { fileId: f.id, deleted: false }, 'File restored')}><Undo2 size={13} /> Restore</button>
                : <button className="btn btn-ghost btn-sm" title="Remove (can be restored)" onClick={() => call('setFileDeleted', { fileId: f.id, deleted: true }, 'File removed — Restore is under "show removed"')}><Trash2 size={13} /></button>}
            </div>
          ))}
          {!req.deletedAt && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <select className="input" style={{ ...inputStyle, width: 'auto' }} value={kind} onChange={(e) => setKind(e.target.value)}>
                {fileKinds.map((k) => <option key={k}>{k}</option>)}
              </select>
              <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => upload(e.target.files)} />
              <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader size={13} className="spin" /> : <Paperclip size={13} />} Attach files
              </button>
              <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>PDF, Excel, Word, images · up to {MAX_MB} MB each</span>
            </div>
          )}

          <div style={sectionTitle}>Timeline</div>
          {events.length === 0 && <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)' }}>No entries yet — log when it was sent, replies, meetings.</div>}
          {events.map((e) => (
            <div key={e.id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderTop: '1px solid var(--hp-border)', opacity: e.deletedAt ? 0.5 : 1, alignItems: 'flex-start' }}>
              <CalendarClock size={14} style={{ color: 'var(--hp-dim)', marginTop: 3 }} />
              <div style={{ width: 96, flexShrink: 0, fontSize: '.82rem', color: 'var(--hp-dim)' }}>{fmtDate(e.date)}</div>
              <div style={{ flex: 1, fontSize: '.86rem', whiteSpace: 'pre-wrap' }}>{e.note}<span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>{e.by ? ` — ${e.by}` : ''}</span></div>
              {e.deletedAt
                ? <button className="btn btn-ghost btn-sm" onClick={() => call('setEventDeleted', { eventId: e.id, deleted: false })}><Undo2 size={13} /></button>
                : <button className="btn btn-ghost btn-sm" title="Remove (can be restored)" onClick={() => call('setEventDeleted', { eventId: e.id, deleted: true })}><Trash2 size={13} /></button>}
            </div>
          ))}
          {!req.deletedAt && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <input type="date" className="input" style={{ ...inputStyle, width: 160 }} value={ev.date} onChange={(e) => setEv((v) => ({ ...v, date: e.target.value }))} />
              <input className="input" style={{ ...inputStyle, flex: '1 1 240px', width: 'auto' }} value={ev.note} placeholder="e.g. Emailed to Shari; AP acknowledged; meeting booked for…"
                onChange={(e) => setEv((v) => ({ ...v, note: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') addEvent(); }} />
              <button className="btn btn-secondary btn-sm" onClick={addEvent}><Plus size={13} /> Add</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {!req.deletedAt && <button className="btn btn-secondary btn-sm" onClick={onEdit}><Pencil size={13} /> Edit details</button>}
            {hiddenCount > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setShowHidden((v) => !v)}>{showHidden ? 'Hide removed' : `Show removed (${hiddenCount})`}</button>}
            {req.deletedAt
              ? <button className="btn btn-ghost btn-sm" onClick={() => call('setDeleted', { deleted: false }, 'Request restored')}><Undo2 size={13} /> Restore request</button>
              : <button className="btn btn-ghost btn-sm" onClick={() => { if (window.confirm('Remove this request? It can be restored, and its files are kept.')) call('setDeleted', { deleted: true }, 'Request removed — tick "Show removed" to restore'); }}><Trash2 size={13} /> Remove request</button>}
          </div>
          <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 8 }}>
            Created {fmtDate(req.createdAt)}{req.createdBy ? ` by ${req.createdBy}` : ''} · updated {fmtDate(req.updatedAt)}{req.updatedBy ? ` by ${req.updatedBy}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContractReviewsPage() {
  const { addNotification } = useApp();
  const notify = useCallback((msg, type = 'success') => {
    if (addNotification) addNotification(msg, type); else if (type === 'error') console.error(msg);
  }, [addNotification]);

  const [requests, setRequests] = useState([]);
  const [statuses, setStatuses] = useState(['Draft']);
  const [fileKinds, setFileKinds] = useState(['Other']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | request
  const [openId, setOpenId] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api('load');
      if (!r.success) throw new Error(r.error || 'Could not load');
      setRequests(r.requests || []); setStatuses(r.statuses || []); setFileKinds(r.fileKinds || []);
      setOpenId((id) => id || (r.requests || []).find((x) => !x.deletedAt)?.id || null);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsert = (req) => setRequests((list) => (list.some((x) => x.id === req.id) ? list.map((x) => (x.id === req.id ? req : x)) : [req, ...list]));

  const visible = useMemo(() => requests
    .filter((r) => showRemoved || !r.deletedAt)
    .slice().sort((a, b) => (b.sentDate || b.createdAt || '').localeCompare(a.sentDate || a.createdAt || '')), [requests, showRemoved]);
  const removedCount = requests.filter((r) => r.deletedAt).length;
  const openCount = requests.filter((r) => !r.deletedAt && !['Agreed', 'Partly agreed', 'Declined', 'Withdrawn'].includes(r.status)).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileSignature size={22} style={{ color: 'var(--hp-gold)' }} /> Contract Review Requests
          </h2>
          <p>Letters asking a customer to review a contract — the letter as sent, the evidence behind it, and what came back{requests.length ? ` · ${openCount} open` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={14} className={loading ? 'spin' : ''} /></button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}><Plus size={14} /> New request</button>
        </div>
      </div>
      <div className="page-body">
        {error && <div className="card" style={{ borderColor: 'var(--hp-red, #ef4444)', color: 'var(--hp-red, #ef4444)', marginBottom: 12 }}>{error}</div>}
        {editing && (
          <RequestForm
            request={editing === 'new' ? null : editing}
            statuses={statuses}
            notify={notify}
            onClose={() => setEditing(null)}
            onSaved={(req) => { upsert(req); setEditing(null); setOpenId(req.id); }}
          />
        )}
        {loading && !requests.length && <div className="card"><Loader size={16} className="spin" /> Loading…</div>}
        {!loading && !visible.length && !editing && (
          <div className="card" style={{ color: 'var(--hp-dim)' }}>No requests yet. Click <strong>New request</strong> to record one and attach its letter.</div>
        )}
        {visible.map((r) => (
          <RequestCard
            key={r.id} req={r} fileKinds={fileKinds} notify={notify}
            open={openId === r.id}
            onToggle={() => setOpenId((id) => (id === r.id ? null : r.id))}
            onEdit={() => setEditing(r)}
            onChanged={upsert}
          />
        ))}
        {removedCount > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => setShowRemoved((v) => !v)}>
            {showRemoved ? 'Hide removed requests' : `Show removed requests (${removedCount})`}
          </button>
        )}
      </div>
    </div>
  );
}
